import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  AlertCircle, CheckCircle2, ChevronRight, ExternalLink,
  Loader2, Music, RotateCcw, Search, Undo2, Youtube,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "steelmora_artist_import_job";
const POLL_MS = 2000;
const CONFIDENCE_THRESHOLD = 0.85;
const STALE_DOWNLOAD_MS = 120000;

// ── helpers ──────────────────────────────────────────────────────────────────

function fmtDuration(secs) {
  if (!secs) return "—";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function fmtViews(n) {
  if (!n) return "";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M views`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K views`;
  return `${n} views`;
}

const ITEM_STATUS = {
  queued:       { label: "En cola",             cls: "bg-muted/20 text-muted-foreground",     icon: null },
  downloading:  { label: "Descargando audio...", cls: "bg-blue-400/15 text-blue-200",       icon: <Loader2 className="h-3.5 w-3.5 animate-spin" /> },
  downloading_audio: { label: "Descargando audio...", cls: "bg-blue-400/15 text-blue-200", icon: <Loader2 className="h-3.5 w-3.5 animate-spin" /> },
  retrying_download: { label: "Reintentando descarga...", cls: "bg-sky-400/15 text-sky-200", icon: <Loader2 className="h-3.5 w-3.5 animate-spin" /> },
  analyzing:    { label: "Analizando acordes...",cls: "bg-signal/15 text-signal",       icon: <Loader2 className="h-3.5 w-3.5 animate-spin" /> },
  transcribing: { label: "Transcribiendo...",    cls: "bg-indigo-400/15 text-indigo-200",   icon: <Loader2 className="h-3.5 w-3.5 animate-spin" /> },
  analyzed:     { label: "Analizada",            cls: "bg-brass/15 text-brass",   icon: null },
  published:    { label: "Publicada",            cls: "bg-tune/15 text-tune", icon: <CheckCircle2 className="h-3.5 w-3.5" /> },
  waiting_captcha: { label: "Verificación humana", cls: "bg-brass/15 text-brass", icon: <AlertCircle className="h-3.5 w-3.5" /> },
  failed:       { label: "Error",                cls: "bg-destructive/15 text-destructive",         icon: <AlertCircle className="h-3.5 w-3.5" /> },
  reverted:     { label: "Revertida",            cls: "bg-brass/15 text-brass",     icon: <Undo2 className="h-3.5 w-3.5" /> },
};

const ACTIVE_ITEM_STATUSES = ["queued", "downloading", "downloading_audio", "retrying_download", "analyzing", "transcribing", "waiting_captcha"];
const TERMINAL_JOB_STATUSES = ["completed", "failed", "cancelled", "paused", "partial"];

const StatusBadge = ({ status }) => {
  const m = ITEM_STATUS[status] || ITEM_STATUS.queued;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.cls}`}>
      {m.icon}{m.label}
    </span>
  );
};

const COMPLEXITY_OPTIONS = [
  { value: "basic",    label: "Básico (mayor/menor)" },
  { value: "standard", label: "Estándar (+ 7, dim)" },
  { value: "extended", label: "Extendido (+ maj7, m7, aug)" },
  { value: "full",     label: "Completo (sin filtro)" },
];

// ── main component ────────────────────────────────────────────────────────────

export default function AdminArtistImportPage() {
  const { getAuthHeaders } = useAuth();

  // "search" → input form
  // "selecting" → showing results, user picks songs
  // "importing" → job running, polling
  // "done" → job complete, review + revert
  const [phase, setPhase] = useState("search");

  // search state
  const [artistQuery, setArtistQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [complexity, setComplexity] = useState("standard");

  // import state
  const [jobId, setJobId] = useState(null);
  const [jobData, setJobData] = useState(null);
  const pollingRef = useRef(null);

  // revert state
  const [reverting, setReverting] = useState({});
  const [captchaAction, setCaptchaAction] = useState(null);
  const [retryingFailed, setRetryingFailed] = useState(false);

  // session recovery
  const [savedJobId, setSavedJobId] = useState(null);
  const [recovering, setRecovering] = useState(false);

  // search error / setup feedback
  const [searchError, setSearchError] = useState(null);
  const [setupRequired, setSetupRequired] = useState(false);

  // selection filter
  const [songFilter, setSongFilter] = useState("");

  // auth status (fetched on mount)
  const [authStatus, setAuthStatus] = useState(null);
  const [uploadingCookies, setUploadingCookies] = useState(false);
  const [cookieSlot, setCookieSlot] = useState("");
  const fileInputRef = useRef(null);

  // import start time for ETA
  const importStartRef = useRef(null);

  // ── on mount: check saved session + auth status ────────────────────────────
  useEffect(() => {
    const saved = localStorage.getItem(LS_KEY);
    if (saved) setSavedJobId(saved);
  }, []);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/admin/artist-import/auth-status`, { headers: getAuthHeaders() })
      .then((res) => { if (!cancelled) setAuthStatus(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [getAuthHeaders]);

  // ── polling ────────────────────────────────────────────────────────────────
  const poll = useCallback(async (jid) => {
    try {
      const res = await axios.get(`${API}/admin/artist-import/jobs/${jid}`, {
        headers: getAuthHeaders(),
      });
      setJobData(res.data);
      const jobStatus = res.data.job?.status;
      const items = res.data.items || [];
      const active = items.some((i) =>
        ACTIVE_ITEM_STATUSES.includes(i.status)
      );
      if (TERMINAL_JOB_STATUSES.includes(jobStatus) || !active) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        setPhase("done");
      }
    } catch {
      // transient — keep polling
    }
  }, [getAuthHeaders]);

  const startPolling = useCallback((jid) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    importStartRef.current = Date.now();
    poll(jid);
    pollingRef.current = setInterval(() => poll(jid), POLL_MS);
  }, [poll]);

  useEffect(() => () => { if (pollingRef.current) clearInterval(pollingRef.current); }, []);

  // ── recover session ────────────────────────────────────────────────────────
  const recoverSession = async () => {
    if (!savedJobId) return;
    setRecovering(true);
    try {
      const res = await axios.get(`${API}/admin/artist-import/jobs/${savedJobId}`, {
        headers: getAuthHeaders(),
      });
      setJobId(savedJobId);
      setJobData(res.data);
      const jobStatus = res.data.job?.status;
      const active = (res.data.items || []).some((i) =>
        ACTIVE_ITEM_STATUSES.includes(i.status)
      );
      const shouldPoll = active && !TERMINAL_JOB_STATUSES.includes(jobStatus);
      setPhase(shouldPoll ? "importing" : "done");
      if (shouldPoll) startPolling(savedJobId);
      setSavedJobId(null);
    } catch {
      toast.error("No se pudo recuperar la sesión anterior.");
      localStorage.removeItem(LS_KEY);
      setSavedJobId(null);
    } finally {
      setRecovering(false);
    }
  };

  // ── search ─────────────────────────────────────────────────────────────────
  const handleSearch = async () => {
    const q = artistQuery.trim();
    if (!q) { toast.error("Escribe el nombre del artista."); return; }
    setSearching(true);
    setSearchError(null);
    setSetupRequired(false);
    setSearchResults([]);
    setSelectedIds(new Set());
    setSongFilter("");
    try {
      const res = await axios.get(`${API}/admin/artist-import/search`, {
        params: { artist: q, max_results: 50 },
        headers: getAuthHeaders(),
        timeout: 35000,
      });
      const songs = res.data.songs || [];
      const warnings = res.data.warnings || [];
      const isSetupRequired = res.data.setup_required === true;

      if (isSetupRequired) {
        setSetupRequired(true);
        return;
      }

      if (songs.length === 0) {
        const msg = warnings[0] || `No se encontraron canciones para "${q}".`;
        setSearchError(msg);
        return;
      }

      setSearchResults(songs);
      setSelectedIds(new Set(songs.filter((s) => !s.in_catalog).map((s) => s.youtube_video_id)));
      setPhase("selecting");
    } catch (err) {
      const msg = err.response?.data?.detail
        || (err.code === "ECONNABORTED" ? "La búsqueda tardó demasiado. Intenta de nuevo." : "Error al buscar. Verifica que el servidor esté activo.");
      setSearchError(msg);
      toast.error(msg);
    } finally {
      setSearching(false);
    }
  };

  const toggleSelect = (videoId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(videoId)) next.delete(videoId);
      else next.add(videoId);
      return next;
    });
  };

  const selectAll = () =>
    setSelectedIds(new Set(searchResults.filter((s) => !s.in_catalog).map((s) => s.youtube_video_id)));

  const selectNone = () => setSelectedIds(new Set());

  // ── start import ───────────────────────────────────────────────────────────
  const startImport = async () => {
    const songs = searchResults.filter(
      (s) => selectedIds.has(s.youtube_video_id) && s.youtube_video_id
    );
    if (!songs.length) {
      toast.error("Selecciona al menos una canción con URL de YouTube.");
      return;
    }
    try {
      const payload = {
        artist_name: artistQuery.trim(),
        chord_complexity: complexity,
        songs: songs.map((s) => ({
          title: s.title,
          artist: s.artist,
          youtube_video_id: s.youtube_video_id,
          youtube_url: s.youtube_url,
          spotify_track_id: s.spotify_track_id || null,
          thumbnail_url: s.thumbnail_url || null,
          duration_seconds: s.duration_seconds || null,
          channel: s.channel || null,
        })),
      };
      const res = await axios.post(`${API}/admin/artist-import/jobs`, payload, {
        headers: getAuthHeaders(),
      });
      const newJobId = res.data.job_id;
      setJobId(newJobId);
      localStorage.setItem(LS_KEY, newJobId);
      setPhase("importing");
      startPolling(newJobId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo iniciar la importación.");
    }
  };

  // ── revert ─────────────────────────────────────────────────────────────────
  const revertSong = async (songId, title) => {
    if (!window.confirm(`¿Revertir "${title}"? La canción volverá a borrador (draft).`)) return;
    setReverting((p) => ({ ...p, [songId]: true }));
    try {
      await axios.post(
        `${API}/admin/artist-import/jobs/${jobId}/revert/${songId}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`"${title}" revertida a borrador.`);
      poll(jobId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo revertir.");
    } finally {
      setReverting((p) => ({ ...p, [songId]: false }));
    }
  };

  // ── new session ────────────────────────────────────────────────────────────
  const openCaptchaVideo = async (item) => {
    if (!jobId || !item?.id) return;
    setCaptchaAction("open");
    try {
      const res = await axios.post(
        `${API}/admin/artist-import/jobs/${jobId}/items/${item.id}/open-captcha`,
        {},
        { headers: getAuthHeaders() }
      );
      if (res.data?.opened) {
        toast.success("Video abierto para verificación humana.");
      } else {
        toast.warning("No se pudo abrir el navegador automáticamente. Abre el video manualmente.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo abrir el video.");
    } finally {
      setCaptchaAction(null);
    }
  };

  const resumeImport = async () => {
    if (!jobId) return;
    setCaptchaAction("resume");
    try {
      const res = await axios.post(
        `${API}/admin/artist-import/jobs/${jobId}/resume`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Importación reanudada (${res.data?.pending_items || 0} pendientes).`);
      setPhase("importing");
      startPolling(jobId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo reanudar la importación.");
    } finally {
      setCaptchaAction(null);
    }
  };

  const retryFailedItems = async () => {
    if (!jobId) return;
    setRetryingFailed(true);
    try {
      const res = await axios.post(
        `${API}/admin/artist-import/jobs/${jobId}/retry-failed`,
        {},
        { headers: getAuthHeaders() }
      );
      const count = res.data?.failed_items || 0;
      if (res.data?.retried) {
        toast.success(`Reintentando ${count} canciones fallidas.`);
        setPhase("importing");
        startPolling(jobId);
      } else {
        toast.info("No hay canciones fallidas para reintentar.");
        poll(jobId);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudieron reintentar los fallidos.");
    } finally {
      setRetryingFailed(false);
    }
  };

  const handleCookieUpload = async (file) => {
    if (!file) return;
    setUploadingCookies(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (cookieSlot.trim()) fd.append("slot", cookieSlot.trim());
      const res = await axios.post(`${API}/admin/artist-import/upload-cookies`, fd, {
        headers: getAuthHeaders(),
      });
      toast.success(`${res.data.cookies_count} cookies activadas${cookieSlot.trim() ? ` (${cookieSlot.trim()})` : ""}`);
      const authRes = await axios.get(`${API}/admin/artist-import/auth-status`, {
        headers: getAuthHeaders(),
      });
      setAuthStatus(authRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error subiendo cookies");
    } finally {
      setUploadingCookies(false);
    }
  };

  const newSession = () => {
    clearInterval(pollingRef.current);
    pollingRef.current = null;
    importStartRef.current = null;
    localStorage.removeItem(LS_KEY);
    setJobId(null);
    setJobData(null);
    setSearchResults([]);
    setSelectedIds(new Set());
    setSearchError(null);
    setSetupRequired(false);
    setSongFilter("");
    setPhase("search");
  };

  // ── derived ────────────────────────────────────────────────────────────────
  const job = jobData?.job || {};
  const items = jobData?.items || [];
  const waitingCaptchaItem = items.find((i) => i.status === "waiting_captcha") || null;
  const hasCaptchaWait = job.status === "waiting_captcha" || Boolean(waitingCaptchaItem);
  const failedCount = job.failed_items || 0;
  const selectedCount = selectedIds.size;
  const availableCount = searchResults.filter((s) => !s.in_catalog).length;

  const etaLabel = (() => {
    if (phase !== "importing" || !importStartRef.current) return "";
    const done = (job.completed_items || 0) + (job.failed_items || 0);
    const rem = (job.total_items || 0) - done;
    if (rem <= 0) return "";
    const secs = done > 0
      ? Math.round(((Date.now() - importStartRef.current) / 1000 / done) * rem)
      : rem * 60;
    return secs < 60 ? `~${secs}s restantes` : `~${Math.ceil(secs / 60)} min restantes`;
  })();

  const hasYouTubeBlockError = items.some(
    (i) => i.status === "failed" && i.error &&
    (i.error.includes("bloqueó") || i.error.includes("cookies") || i.error.includes("YTDLP"))
  );

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <section className="steel-page space-y-5">
      {/* Header */}
      <header className="flex flex-col gap-4 rounded-3xl border border-border bg-white/[0.045] p-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-signal/65">Administration</p>
          <h1 className="mt-1 text-3xl font-bold text-foreground">Artist Import</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Busca un artista, selecciona sus canciones y publícalas en el catálogo de YouTube Practice.
          </p>
        </div>
        <nav className="flex gap-2">
          <Link
            to="/admin/bulk-import"
            className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-border px-4 text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronRight className="h-4 w-4 rotate-180" /> Bulk Import
          </Link>
          <Link
            to="/admin/music-catalog"
            className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-border px-4 text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronRight className="h-4 w-4 rotate-180" /> Catálogo
          </Link>
          {(phase === "importing" || phase === "done") && (
            <button
              onClick={newSession}
              className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-border px-4 text-sm text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="h-4 w-4" /> Nueva importación
            </button>
          )}
        </nav>
      </header>

      {/* Recover session banner */}
      {savedJobId && phase === "search" && (
        <div className="flex flex-col gap-3 rounded-2xl border border-brass/20 bg-brass/[0.06] p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <RotateCcw className="h-5 w-5 shrink-0 text-brass" />
            <p className="text-sm text-brass">Tienes una importación guardada. ¿Continuar?</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { localStorage.removeItem(LS_KEY); setSavedJobId(null); }}
              className="rounded-lg px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
            >
              Descartar
            </button>
            <button
              onClick={recoverSession}
              disabled={recovering}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brass/20 px-3 py-1.5 text-xs font-semibold text-brass hover:bg-brass/30 disabled:opacity-50"
            >
              {recovering && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Reanudar
            </button>
          </div>
        </div>
      )}

      {/* ── FASE 1: BÚSQUEDA ─────────────────────────────────────────────── */}
      {phase === "search" && (
        <div className="rounded-2xl border border-border bg-panel p-6 space-y-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Nombre del artista
              </label>
              <input
                value={artistQuery}
                onChange={(e) => setArtistQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="Los Tepoz, Banda MS, Natanael Cano..."
                className="w-full rounded-xl border border-border bg-ink/40 px-4 py-2.5 text-foreground outline-none focus:border-signal/50"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={searching || !artistQuery.trim()}
              className="inline-flex items-center gap-2 rounded-xl bg-signal px-6 py-2.5 text-sm font-semibold text-ink hover:bg-signal disabled:opacity-50"
            >
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              {searching ? "Buscando..." : "Buscar"}
            </button>
          </div>

          {/* Panel de autenticación YouTube — soporta pool de cuentas dedicadas */}
          <div className="rounded-xl border border-border bg-panel p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold text-foreground">
                  Autenticación YouTube {authStatus?.method === "cookie_pool" && `— pool (${authStatus.accounts?.length || 0} cuenta${authStatus.accounts?.length === 1 ? "" : "s"})`}
                </p>
                {authStatus?.method !== "cookie_pool" && (
                  authStatus?.configured ? (
                    <p className={`text-xs mt-0.5 ${
                      !authStatus.file_exists
                        ? "text-destructive"
                        : authStatus.file_age_days > 3
                          ? "text-brass"
                          : "text-tune"
                    }`}>
                      {!authStatus.file_exists
                        ? "Archivo no encontrado — sube cookies nuevas"
                        : authStatus.file_age_days > 3
                          ? `Cookies de ${authStatus.file_age_days} días — renovar pronto`
                          : `Activas (${authStatus.file_age_days}d)`}
                    </p>
                  ) : (
                    <p className="text-xs text-destructive mt-0.5">Sin cookies — las descargas fallarán</p>
                  )
                )}
              </div>
              <div className="flex items-center gap-2">
                <input
                  value={cookieSlot}
                  onChange={(e) => setCookieSlot(e.target.value)}
                  placeholder="Nombre de cuenta (opcional)"
                  title="Dejalo vacío para reemplazar la cuenta única legacy. Ponele un nombre (ej. cuenta-1) para agregarla al pool de rotación."
                  className="w-44 rounded-lg border border-border bg-ink/40 px-2.5 py-1.5 text-xs text-foreground outline-none focus:border-signal/50"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingCookies}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-signal/15 px-3 py-1.5 text-xs font-semibold text-signal hover:bg-signal/25 disabled:opacity-50 shrink-0"
                >
                  {uploadingCookies ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                  {uploadingCookies ? "Subiendo..." : "Subir cookies"}
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleCookieUpload(f);
                  e.target.value = "";
                }}
              />
            </div>

            {/* Estado por cuenta cuando hay pool configurado */}
            {authStatus?.method === "cookie_pool" && (
              <div className="space-y-1.5">
                {(authStatus.accounts || []).map((acc) => (
                  <div key={acc.slot} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-ink/30 px-3 py-1.5">
                    <span className="text-xs font-medium text-foreground">{acc.slot}</span>
                    <span className={`text-xs ${
                      !acc.file_exists ? "text-destructive"
                        : acc.cooling_down ? "text-brass"
                        : acc.file_age_days > 3 ? "text-brass"
                        : "text-tune"
                    }`}>
                      {!acc.file_exists
                        ? "sin archivo"
                        : acc.cooling_down
                          ? `en pausa ~${Math.ceil(acc.cooldown_seconds_remaining / 60)}min (anti-bot)`
                          : acc.file_age_days > 3
                            ? `${acc.file_age_days}d — renovar pronto`
                            : `activa (${acc.file_age_days}d)`}
                    </span>
                  </div>
                ))}
                {!(authStatus.accounts || []).length && (
                  <p className="text-xs text-destructive">Sin cuentas — sube al menos una con nombre de cuenta.</p>
                )}
              </div>
            )}

            {/* Instrucciones cuando no hay cookies */}
            {!authStatus?.configured && (
              <div className="text-xs text-muted-foreground space-y-1 border-t border-border pt-3">
                <p className="font-medium text-muted-foreground">Cómo obtener las cookies (30 segundos):</p>
                <ol className="list-decimal list-inside space-y-1 ml-1">
                  <li>
                    Instala{" "}
                    <a
                      href="https://chromewebstore.google.com/detail/get-cookiestxt-locally/cclelndahbckbenkjhflpdbgdldlbecc"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-signal hover:underline"
                    >
                      "Get cookies.txt LOCALLY"
                    </a>{" "}
                    en Chrome
                  </li>
                  <li>Usá una cuenta de Google dedicada, no tu cuenta personal</li>
                  <li>Ve a youtube.com y asegúrate de estar logueado</li>
                  <li>Haz click en la extensión → Export</li>
                  <li>Ponele un nombre de cuenta arriba (ej. "cuenta-1") y subí el archivo</li>
                </ol>
                <p className="text-muted-foreground mt-1">Las cookies duran 1-3 días. Con varias cuentas nombradas, el sistema rota entre ellas automáticamente.</p>
              </div>
            )}
          </div>

          {searchError && (
            <p className="flex items-center gap-2 rounded-xl border border-destructive/20 bg-destructive/[0.06] px-4 py-2.5 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" /> {searchError}
            </p>
          )}

          {setupRequired && (
            <div className="rounded-2xl border border-brass/20 bg-brass/[0.06] p-5 space-y-3">
              <p className="text-sm font-semibold text-brass">
                ⚙️ Configuración requerida para búsqueda de artistas
              </p>
              <p className="text-sm text-muted-foreground">
                Necesitas configurar al menos una fuente de búsqueda en el servidor.
              </p>
              <div className="space-y-1 text-sm">
                <p className="font-medium text-foreground">YouTube Data API v3 (gratis, recomendado):</p>
                <ol className="list-decimal list-inside space-y-1 text-muted-foreground ml-2 text-xs">
                  <li>Ve a <span className="text-signal">console.cloud.google.com</span></li>
                  <li>APIs &amp; Services → YouTube Data API v3 → Enable</li>
                  <li>Credentials → Create API Key</li>
                  <li>Agrega en <code className="text-brass">backend/.env</code>: <code className="text-brass">YOUTUBE_API_KEY=tu_key</code></li>
                  <li>Reinicia el servidor backend</li>
                </ol>
                <p className="text-xs text-muted-foreground mt-1">Cuota gratuita: 10,000 unidades/día (~99 búsquedas/día).</p>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Fuentes:</span>
              <span className="rounded-lg bg-destructive/20 px-2.5 py-1 text-xs font-semibold text-destructive flex items-center gap-1">
                <Youtube className="h-3 w-3" /> YouTube
              </span>
              <span className="rounded-lg bg-tune/20 px-2.5 py-1 text-xs font-semibold text-tune">
                ♫ Spotify
              </span>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground">Complejidad de acordes</label>
              <select
                value={complexity}
                onChange={(e) => setComplexity(e.target.value)}
                className="rounded-lg border border-border bg-ink/50 px-2 py-1.5 text-xs text-foreground"
              >
                {COMPLEXITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* ── FASE 2: SELECCIÓN ────────────────────────────────────────────── */}
      {phase === "selecting" && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-panel px-5 py-3">
            <div>
              <p className="text-base font-semibold text-foreground">
                {artistQuery} — {searchResults.length} canciones encontradas
              </p>
              <p className="text-xs text-muted-foreground">
                {selectedCount} seleccionadas / {availableCount} disponibles
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={selectAll} className="text-xs text-signal hover:text-signal">☑ Todas</button>
              <button onClick={selectNone} className="text-xs text-muted-foreground hover:text-foreground">☐ Ninguna</button>
              <div className="flex items-center gap-2">
                <label className="text-xs text-muted-foreground">Acordes</label>
                <select
                  value={complexity}
                  onChange={(e) => setComplexity(e.target.value)}
                  className="rounded-lg border border-border bg-ink/50 px-2 py-1 text-xs text-foreground"
                >
                  {COMPLEXITY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={newSession}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                ← Nueva búsqueda
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 rounded-xl border border-border bg-ink/40 px-3">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <input
              value={songFilter}
              onChange={(e) => setSongFilter(e.target.value)}
              placeholder="Filtrar canciones..."
              className="h-10 flex-1 bg-transparent text-sm text-foreground outline-none"
            />
            {songFilter && (
              <button onClick={() => setSongFilter("")} className="text-muted-foreground hover:text-foreground text-xs px-1">✕</button>
            )}
          </div>

          <div className="rounded-2xl border border-border bg-panel overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 w-8" />
                  <th className="px-4 py-3">Canción</th>
                  <th className="px-4 py-3">Duración</th>
                  <th className="px-4 py-3">Views</th>
                  <th className="px-4 py-3">Canal</th>
                  <th className="px-4 py-3">Estado</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.filter((s) => !songFilter || s.title.toLowerCase().includes(songFilter.toLowerCase())).map((song) => {
                  const isDisabled = song.in_catalog || !song.youtube_video_id;
                  const isSelected = selectedIds.has(song.youtube_video_id);
                  return (
                    <tr
                      key={song.youtube_video_id || `${song.source ?? ''}-${song.title}`}
                      className={`border-b border-border last:border-0 ${isDisabled ? "opacity-50" : "cursor-pointer hover:bg-panel/60"}`}
                      onClick={() => !isDisabled && toggleSelect(song.youtube_video_id)}
                    >
                      <td className="px-4 py-2.5">
                        <input
                          type="checkbox"
                          checked={isSelected && !isDisabled}
                          disabled={isDisabled}
                          onChange={() => !isDisabled && toggleSelect(song.youtube_video_id)}
                          className="accent-signal"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          {song.thumbnail_url && (
                            <img
                              src={song.thumbnail_url}
                              alt=""
                              className="h-9 w-16 rounded object-cover opacity-80"
                            />
                          )}
                          <div>
                            <p className="font-medium text-foreground flex items-center gap-1.5">
                              {song.title}
                              {song.source === "youtube" && <span className="rounded bg-destructive/20 px-1.5 py-0.5 text-[10px] font-bold text-destructive leading-none">YT</span>}
                              {song.source === "spotify" && <span className="rounded bg-tune/20 px-1.5 py-0.5 text-[10px] font-bold text-tune leading-none">SP</span>}
                            </p>
                            {song.youtube_video_id && (
                              <a
                                href={song.youtube_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-xs text-destructive hover:text-destructive flex items-center gap-1"
                              >
                                <Youtube className="h-3 w-3" />{song.youtube_video_id}
                              </a>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground">{fmtDuration(song.duration_seconds)}</td>
                      <td className="px-4 py-2.5 text-muted-foreground text-xs">{fmtViews(song.view_count)}</td>
                      <td className="px-4 py-2.5 text-muted-foreground text-xs max-w-[160px] truncate">{song.channel}</td>
                      <td className="px-4 py-2.5">
                        {song.in_catalog ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-tune/15 px-2.5 py-0.5 text-xs font-semibold text-tune">
                            <CheckCircle2 className="h-3 w-3" /> Ya en catálogo
                          </span>
                        ) : !song.youtube_video_id ? (
                          <span className="text-xs text-muted-foreground">Sin URL YouTube</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Disponible</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end">
            <button
              onClick={startImport}
              disabled={selectedCount === 0}
              className="inline-flex items-center gap-2 rounded-xl bg-signal px-8 py-3 text-sm font-semibold text-ink hover:bg-signal disabled:opacity-40"
            >
              <Music className="h-4 w-4" />
              Iniciar importación ({selectedCount} canciones)
            </button>
          </div>
        </>
      )}

      {/* ── FASE 3: PROGRESO ─────────────────────────────────────────────── */}
      {phase === "importing" && jobData && (
        <>
          <div className="rounded-2xl border border-border bg-panel p-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1">
              <Loader2 className="h-5 w-5 shrink-0 animate-spin text-signal" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground">
                  Importando: {job.artist_name} — {job.completed_items + (job.failed_items || 0)}/{job.total_items}
                </p>
                <p className="text-xs text-muted-foreground">
                  Job guardado — puedes cerrar el tab y volver.
                  {etaLabel && <span className="ml-2 font-semibold text-signal">{etaLabel}</span>}
                </p>
              </div>
            </div>
            <div className="w-40 h-2 rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full rounded-full bg-signal transition-all duration-500"
                style={{ width: `${job.total_items ? ((job.completed_items + (job.failed_items || 0)) / job.total_items) * 100 : 0}%` }}
              />
            </div>
          </div>
          {hasCaptchaWait && (
            <HumanVerificationPanel
              item={waitingCaptchaItem}
              action={captchaAction}
              onOpen={() => openCaptchaVideo(waitingCaptchaItem)}
              onResume={resumeImport}
            />
          )}
          {hasYouTubeBlockError && <YouTubeBlockBanner />}
          <ImportItemsTable items={items} jobId={jobId} reverting={reverting} onRevert={revertSong} showRevert={false} />
        </>
      )}

      {/* ── FASE 4: REVISIÓN FINAL ───────────────────────────────────────── */}
      {phase === "done" && jobData && (
        <>
          <div className="grid gap-3 sm:grid-cols-4">
            {[
              { label: "Total",      value: job.total_items || 0,     cls: "text-foreground" },
              { label: "Completadas",value: job.completed_items || 0, cls: "text-brass" },
              { label: "Publicadas", value: job.published_items || 0, cls: "text-tune" },
              { label: "Con error",  value: job.failed_items || 0,    cls: "text-destructive" },
            ].map(({ label, value, cls }) => (
              <article key={label} className="rounded-2xl border border-border bg-white/[0.045] p-4">
                <p className={`text-3xl font-bold ${cls}`}>{value}</p>
                <p className="text-sm text-muted-foreground">{label}</p>
              </article>
            ))}
          </div>

          {hasCaptchaWait && (
            <HumanVerificationPanel
              item={waitingCaptchaItem}
              action={captchaAction}
              onOpen={() => openCaptchaVideo(waitingCaptchaItem)}
              onResume={resumeImport}
            />
          )}
          {hasYouTubeBlockError && <YouTubeBlockBanner />}
          <ImportItemsTable items={items} jobId={jobId} reverting={reverting} onRevert={revertSong} showRevert />

          <div className="flex flex-wrap gap-3">
            <Link
              to="/admin/music-catalog"
              className="inline-flex items-center gap-2 rounded-xl bg-tune px-6 py-3 text-sm font-semibold text-foreground hover:bg-tune"
            >
              <ExternalLink className="h-4 w-4" /> Ver en catálogo
            </Link>
            {failedCount > 0 && (
              <button
                onClick={retryFailedItems}
                disabled={retryingFailed}
                className="inline-flex items-center gap-2 rounded-xl bg-destructive px-5 py-3 text-sm font-semibold text-ink hover:bg-destructive/90 disabled:opacity-50"
              >
                {retryingFailed ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RotateCcw className="h-4 w-4" />
                )}
                Reintentar fallidas ({failedCount})
              </button>
            )}
            <button
              onClick={newSession}
              className="inline-flex items-center gap-2 rounded-xl border border-border px-5 py-3 text-sm text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="h-4 w-4" /> Nueva importación
            </button>
          </div>
        </>
      )}
    </section>
  );
}

// ── YouTubeBlockBanner sub-component ─────────────────────────────────────────

function HumanVerificationPanel({ item, action, onOpen, onResume }) {
  const disabled = !item;

  return (
    <div className="rounded-2xl border border-brass/25 bg-brass/[0.07] p-4 space-y-3">
      <div className="flex items-start gap-3">
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-brass" />
        <div className="space-y-1">
          <p className="text-sm font-semibold text-brass">Verificación humana requerida</p>
          <p className="text-xs text-muted-foreground">
            YouTube pidió confirmar que no eres un robot. Resuelve el CAPTCHA en Chrome,
            vuelve a exportar cookies si YouTube lo requiere y pulsa Reanudar.
          </p>
          {item?.title && (
            <p className="text-xs text-muted-foreground">Video pausado: <span className="text-foreground/80">{item.title}</span></p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          onClick={onOpen}
          disabled={disabled || action === "open"}
          className="inline-flex min-h-9 items-center gap-2 rounded-lg border border-brass/30 px-3 text-xs font-semibold text-brass hover:bg-brass/10 disabled:opacity-50"
        >
          {action === "open" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Youtube className="h-3.5 w-3.5" />}
          Abrir video
        </button>
        <button
          onClick={onResume}
          disabled={action === "resume"}
          className="inline-flex min-h-9 items-center gap-2 rounded-lg bg-brass px-3 text-xs font-semibold text-ink hover:bg-brass disabled:opacity-50"
        >
          {action === "resume" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Reanudar importación
        </button>
      </div>
    </div>
  );
}

function YouTubeBlockBanner() {
  return (
    <div className="rounded-2xl border border-brass/20 bg-brass/[0.06] p-4 space-y-2">
      <p className="text-sm font-semibold text-brass">
        ⚠️ YouTube está bloqueando las descargas
      </p>
      <p className="text-xs text-muted-foreground">
        Para solucionarlo, agrega esta línea en <code className="text-brass">backend/.env</code>:
      </p>
      <code className="block text-xs bg-ink/50 rounded-lg p-2.5 text-signal font-mono">
        YTDLP_COOKIES_FROM_BROWSER=chrome
      </code>
      <p className="text-xs text-muted-foreground">
        Reinicia el servidor backend, luego crea un nuevo job con las canciones que fallaron.
        Chrome debe estar abierto y con sesión iniciada en YouTube.
      </p>
      <p className="text-xs text-muted-foreground">
        Alternativas: <code className="text-muted-foreground">firefox</code> · <code className="text-muted-foreground">edge</code> · <code className="text-muted-foreground">brave</code>
      </p>
    </div>
  );
}

// ── ImportItemsTable sub-component ────────────────────────────────────────────

function ImportItemsTable({ items, jobId: _jobId, reverting, onRevert, showRevert }) {
  return (
    <div className="rounded-2xl border border-border bg-panel overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <th className="px-4 py-3">Canción</th>
            <th className="px-4 py-3">Key</th>
            <th className="px-4 py-3">BPM</th>
            <th className="px-4 py-3">Acordes</th>
            <th className="px-4 py-3">Estado / Progreso</th>
            {showRevert && <th className="px-4 py-3 w-24" />}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <ItemProgressRow
              key={item.id}
              item={item}
              showRevert={showRevert}
              reverting={!!reverting[item.song_id]}
              onRevert={() => onRevert(item.song_id, item.title)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ItemProgressRow({ item, showRevert, reverting, onRevert }) {
  const canRevert = showRevert && item.status === "published" && item.song_id;
  const activeDownload = ["downloading", "downloading_audio", "retrying_download"].includes(item.status);
  const updatedAt = item.updated_at ? new Date(item.updated_at).getTime() : null;
  const isStaleDownload = activeDownload && updatedAt && Date.now() - updatedAt > STALE_DOWNLOAD_MS;
  const timeoutFailed = item.status === "failed" && item.progress_stage === "download_timeout";
  const progressStatuses = [
    "downloading",
    "downloading_audio",
    "retrying_download",
    "analyzing",
    "transcribing",
    "waiting_captcha",
  ];

  return (
    <>
      <tr key={`${item.id}-main`} className="border-b border-border last:border-0">
        <td className="px-4 py-2.5">
          <p className="font-medium text-foreground">{item.title}</p>
          <p className="text-xs text-muted-foreground">{item.artist_name}</p>
        </td>
        <td className="px-4 py-2.5 text-muted-foreground">{item.key || "—"}</td>
        <td className="px-4 py-2.5 text-muted-foreground">{item.bpm || "—"}</td>
        <td className="px-4 py-2.5">
          {item.unique_chords?.length ? (
            <span className="flex flex-wrap gap-1">
              {item.unique_chords.slice(0, 5).map((c) => (
                <span key={c} className="rounded-md bg-signal/10 px-1.5 py-0.5 text-[11px] font-semibold text-signal">{c}</span>
              ))}
              {item.unique_chords.length > 5 && (
                <span className="text-[11px] text-muted-foreground">+{item.unique_chords.length - 5}</span>
              )}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
        <td className="px-4 py-2.5">
          <div className="flex flex-col gap-1">
            <StatusBadge status={item.status} />
            {item.progress_stage && progressStatuses.includes(item.status) && (
              <span className="text-xs text-muted-foreground">{item.progress_stage}</span>
            )}
            {isStaleDownload && (
              <span className="text-xs text-brass">
                Descarga tardando demasiado, reintentando o saltando canción...
              </span>
            )}
            {timeoutFailed && (
              <span className="text-xs text-destructive">Timeout de descarga, canción saltada</span>
            )}
          </div>
        </td>
        {showRevert && (
          <td className="px-4 py-2.5">
            {canRevert && (
              <button
                onClick={onRevert}
                disabled={reverting}
                className="inline-flex items-center gap-1.5 rounded-lg border border-brass/30 px-2.5 py-1 text-xs font-semibold text-brass hover:bg-brass/10 disabled:opacity-40"
              >
                {reverting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />}
                Revertir
              </button>
            )}
          </td>
        )}
      </tr>
      {["failed", "waiting_captcha"].includes(item.status) && item.error && (
        <tr key={`${item.id}-error`} className="border-b border-border">
          <td colSpan={showRevert ? 6 : 5} className="px-4 pb-3 pt-0">
            <p className={`rounded-lg px-3 py-2 text-xs ${item.status === "waiting_captcha" ? "bg-brass/[0.08] text-brass" : "bg-destructive/[0.08] text-destructive"}`}>{item.error}</p>
          </td>
        </tr>
      )}
    </>
  );
}
